import React from 'react'
import { Users, ShoppingCart } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface CompanyItem {
  name: string
  logo?: string
}

export interface SellerItem {
  name: string
  logo?: string
  logoHeight?: string
  url: string
}

interface TrustedByProps {
  className?: string
  companies?: CompanyItem[]
  sellers?: SellerItem[]
}

const defaultCompanies: CompanyItem[] = [
  { name: 'Google', logo: '/images/companies/google.svg' },
  { name: 'Microsoft', logo: '/images/companies/microsoft.svg' },
  { name: 'Databricks', logo: '/images/companies/databricks.svg' },
  { name: 'Oxford University', logo: '/images/companies/oxford.svg' },
  { name: 'Citi', logo: '/images/companies/citi.svg' },
  { name: 'ING Bank' },
  { name: 'eToro' },
  { name: 'HERE Technologies' },
]

const CompanyBadge = ({ company }: { company: CompanyItem }) => {
  return (
    <span className="group inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-tertiary hover:text-secondary transition-colors duration-300">
      {company.logo && (
        <img
          src={company.logo}
          alt=""
          className="h-5 opacity-40 grayscale group-hover:opacity-70 transition-all duration-300"
        />
      )}
      {company.name}
    </span>
  )
}

const SellerBadge = ({ seller }: { seller: SellerItem }) => {
  return (
    <a
      href={seller.url}
      target="_blank"
      rel="noreferrer"
      className="group inline-flex items-center gap-2 text-base font-semibold tracking-wide uppercase text-tertiary hover:text-secondary transition-colors duration-300"
      title={seller.name}
    >
      {seller.logo && (
        <img
          src={seller.logo}
          alt=""
          className={`${seller.logoHeight || 'h-8'} grayscale opacity-50 group-hover:opacity-80 transition-all duration-300 dark:invert dark:opacity-60 dark:group-hover:opacity-90`}
        />
      )}
      {seller.name}
    </a>
  )
}

export const TrustedBy: React.FC<TrustedByProps> = ({
  className = '',
  companies = defaultCompanies,
  sellers,
}) => {
  const { t } = useTranslation('home')

  if (companies.length === 0 && (!sellers || sellers.length === 0)) return null

  return (
    <div className={`w-full text-center border-t border-secondary pt-8 ${className}`}>
      {companies.length > 0 && (
        <div>
          <p className="text-xs text-tertiary flex items-center justify-center gap-1.5 mb-3">
            <Users className="w-3 h-3" />
            <span>{t('bookStats.trustedBy')}</span>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {companies.map((company, i) => (
              <CompanyBadge key={i} company={company} />
            ))}
          </div>
        </div>
      )}

      {sellers && sellers.length > 0 && (
        <div className={companies.length > 0 ? 'mt-6' : ''}>
          <p className="text-xs text-tertiary flex items-center justify-center gap-1.5 mb-3">
            <ShoppingCart className="w-3 h-3" />
            <span>{t('purchaseSellers.title')}</span>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {sellers.map((seller, i) => (
              <SellerBadge key={i} seller={seller} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default TrustedBy
